import { useState, useEffect } from "react";
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { countries } from 'countries-list';
import { MultiSelect } from "react-multi-select-component";
import Logo from './logo.png';
import './checkout.css';

/*
 * CHECKOUT — public, pre-login.
 *
 * Every request, payload and redirect below is unchanged. What changed is what
 * the buyer sees:
 *   - the product name used to be dark text on a dark panel and was barely legible
 *   - the feature bullets rendered as a broken glyph: the markup injected byte
 *     0x13, a control character, where a bullet was meant to be
 *   - the price appeared only inside the button, on a page asking for 420 EUR
 *   - two alert() dialogs carried the only failure messaging
 *   - the submit button could be pressed repeatedly while a checkout session
 *     was being created
 */

const Checkout = () => {
  const { uuid } = useParams();

  const [selected, setSelected] = useState([]);
  const [data, setData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [err, setErr] = useState(false);
  const [appCountries, setAppCountries] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [note, setNote] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  const [record, setRecord] = useState({
    FullName: '',
    VatNumber: '',
    Password: '',
    Email: '',
    Country: '',
    Telephone: '',
    telPrefix: ''
  });

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* The page owns the whole viewport while it is mounted. */
  useEffect(() => {
    document.documentElement.classList.add('checkout-page');
    document.body.classList.add('checkout-page');
    return () => {
      document.documentElement.classList.remove('checkout-page');
      document.body.classList.remove('checkout-page');
    };
  }, []);

  const loadData = () => {
    setIsLoading(true);
    axios.get(process.env.REACT_APP_API_SERVER + 'manager/eshopproducts/' + uuid)
      .then(res => {
        if (res.data.err === 0 && res.data.results.length === 1) {
          setErr(false);
          setData(res.data.results[0]);
          loadCountries();

          /* Coming back from a cancelled payment: restore what was typed. */
          const saved = sessionStorage.getItem('record');
          if (saved) {
            try {
              const parsed = JSON.parse(saved);
              setRecord(parsed);
              if (parsed.appCountries) setSelected(parsed.appCountries);
            } catch (xerr) { /* nothing usable to restore */ }
          }
        } else {
          setErr(true);
        }
        setIsLoading(false);
      })
      .catch(() => {
        setErr(true);
        setIsLoading(false);
      });
  };

  const loadCountries = () => {
    axios.get(process.env.REACT_APP_API_SERVER + 'manager/eshopcountries')
      .then(res => {
        if (res.data.err === 0) setAppCountries(res.data.results);
      })
      .catch(() => { /* the plan may not need a country choice at all */ });
  };

  const countriesList = Object.entries(countries).map((item, index) => (
    <option key={'country' + index} value={item[1].name}>{item[1].name}</option>
  ));

  const appCountriesList = appCountries.map((item) => ({
    label: item.Country,
    value: item.CountryID
  }));

  const selectCountry = (countryName) => {
    let telPrefix;
    try {
      telPrefix = Object.entries(countries).filter(item => item[1].name === countryName)[0][1].phone[0];
    } catch (xerr) {
      telPrefix = '';
    }
    setRecord({ ...record, Country: countryName, telPrefix });
  };

  /* The plan caps how many countries may be picked. */
  useEffect(() => {
    if (selected.length > data.NumberOfCountries) {
      const trimmed = selected.slice(0, data.NumberOfCountries);
      setSelected(trimmed);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  const register = (e) => {
    e.preventDefault();
    if (submitting) return;

    if (data.NumberOfCountries > 0 && selected.length !== data.NumberOfCountries) {
      setNote({
        tone: 'bad',
        title: 'Choose your countries',
        text: 'This plan includes ' + data.NumberOfCountries + (data.NumberOfCountries === 1 ? ' country' : ' countries') + '. Pick exactly that many to continue.'
      });
      return;
    }

    setNote(null);
    setSubmitting(true);

    axios.get(process.env.REACT_APP_API_SERVER + 'manager/eshopproducts/checkemail/' + record.Email)
      .then(xres => {
        if (xres.data.err !== 0) {
          setSubmitting(false);
          setNote({
            tone: 'bad',
            title: 'That email already has an account',
            text: 'Sign in instead, or use a different email address for this purchase.'
          });
          return;
        }

        axios.get(process.env.REACT_APP_API_SERVER + 'manager/eshopproducts/paymentlink/' + uuid)
          .then(res => {
            if (res.data.err === 0) {
              const regObj = data.NumberOfCountries > 0
                ? { ...record, LicenceDurationMonths: data.LicenceDurationMonths, appCountries: selected }
                : { ...record, appCountries: appCountriesList };

              sessionStorage.setItem('record', JSON.stringify(regObj));
              window.location.href = res.data.url;
            } else {
              setSubmitting(false);
              setNote({
                tone: 'bad',
                title: 'Payment could not be started',
                text: 'The payment provider did not return a checkout link. Please try again in a moment.'
              });
            }
          })
          .catch(() => {
            setSubmitting(false);
            setNote({
              tone: 'bad',
              title: 'Payment could not be started',
              text: 'We could not reach the payment provider. Check your connection and try again.'
            });
          });
      })
      .catch(() => {
        setSubmitting(false);
        setNote({
          tone: 'bad',
          title: 'We could not check that email',
          text: 'The server did not answer. Please try again in a moment.'
        });
      });
  };

  /* The API sends Descr as an HTML <ul>. Take the text of each item and render
     it as real markup rather than injecting html with a control character in it. */
  const features = (() => {
    const raw = data.Descr ? String(data.Descr) : '';
    if (!raw) return [];
    const items = raw.match(/<li>([\s\S]*?)<\/li>/gi) || [];
    return items
      .map(x => x.replace(/<[^>]*>/g, '').replace(/&amp;/g, '&').trim())
      .filter(Boolean);
  })();

  const term = data.LicenceDurationMonths
    ? (Number(data.LicenceDurationMonths) >= 12
      ? 'for 12 months'
      : 'for ' + data.LicenceDurationMonths + ' months')
    : '';

  const masthead = (
    <header className="co-top">
      <div className="co-brand">
        <img src={Logo} alt="123High" />
        <span>
          <span className="co-brand-name">123High</span>
          <span className="co-brand-sub" style={{ display: 'block' }}>manager</span>
        </span>
      </div>
      <span className="co-secure">
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <rect x="4" y="10" width="16" height="10" rx="2" />
          <path d="M8 10V7a4 4 0 0 1 8 0v3" />
        </svg>
        Secure payment via Stripe
      </span>
    </header>
  );

  if (err) {
    return (
      <div className="co-page">
        {masthead}
        <div className="co-center">
          <div className="co-card-msg">
            <div className="co-mark">
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 8v5M12 17h.01" /><circle cx="12" cy="12" r="9" /></svg>
            </div>
            <h2>This plan is not available</h2>
            <p>The link may be out of date. Ask for a current one, or choose a plan from your account.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="co-page">
      {masthead}

      <div className="co-body">

        {/* ---------- what is being bought ---------- */}
        <section className="co-summary" aria-label="Your plan">
          {isLoading ? (
            <div style={{ padding: 20 }} className="co-sk">
              <div className="skeleton co-sk-title"></div>
              <div className="skeleton co-sk-line"></div>
              <div className="skeleton co-sk-line"></div>
            </div>
          ) : (
            <>
              <div className="co-sum-head">
                <div className="mono co-sport">{data.SportName}</div>
                <h1 className="co-title">{data.Title}</h1>
              </div>

              <div className="co-price-row">
                <span className="co-price">{data.Price}€</span>
                {term && <span className="co-term">{term}</span>}
                <span className="mono co-once">one payment</span>
              </div>

              {features.length > 0 && (
                <ul className="co-feats">
                  {features.map((f, i) => (
                    <li className="co-feat" key={i}>
                      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 12.5l5 5L20 7" /></svg>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </section>

        {/* ---------- the form ---------- */}
        <section className="co-form" aria-label="Your details">
          <h2>Create your account</h2>
          <p className="co-form-sub">You will be taken to Stripe to pay. Your account is created once the payment completes.</p>

          <form onSubmit={register}>
            <div className="co-grid">

              <div className="co-field co-field-wide">
                <label className="co-label" htmlFor="co-name">Full name</label>
                <input id="co-name" className="co-input" type="text" value={record.FullName}
                  onChange={(e) => setRecord({ ...record, FullName: e.target.value })} required />
              </div>

              <div className="co-field">
                <label className="co-label" htmlFor="co-vat">VAT number</label>
                <input id="co-vat" className="co-input" type="text" value={record.VatNumber}
                  onChange={(e) => setRecord({ ...record, VatNumber: e.target.value })} required />
              </div>

              <div className="co-field">
                <label className="co-label" htmlFor="co-country">Country</label>
                <select id="co-country" className="co-input" value={record.Country}
                  onChange={(e) => selectCountry(e.target.value)} required>
                  <option value="">Select your country</option>
                  {countriesList}
                </select>
              </div>

              <div className="co-field">
                <label className="co-label" htmlFor="co-email">Email</label>
                <input id="co-email" className="co-input" type="email" value={record.Email}
                  onChange={(e) => setRecord({ ...record, Email: e.target.value })} required />
              </div>

              <div className="co-field">
                <label className="co-label" htmlFor="co-phone">Phone</label>
                <input id="co-phone" className="co-input" type="tel" value={record.Telephone}
                  onChange={(e) => setRecord({ ...record, Telephone: e.target.value })} required />
              </div>

              <div className="co-field co-field-wide">
                <label className="co-label" htmlFor="co-pass">Password</label>
                <span className="co-pass">
                  <input id="co-pass" className="co-input" type={showPassword ? 'text' : 'password'}
                    value={record.Password} minLength={6} maxLength={12} required
                    onChange={(e) => setRecord({ ...record, Password: e.target.value })} />
                  <button type="button" className="co-eye" onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}>
                    {showPassword ? (
                      <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M2 12s3.6-6.5 10-6.5S22 12 22 12s-3.6 6.5-10 6.5S2 12 2 12z" /><circle cx="12" cy="12" r="3" />
                      </svg>
                    ) : (
                      <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 3l18 18M10.6 10.7a3 3 0 0 0 4.2 4.2M9.9 5.6A9.7 9.7 0 0 1 12 5.5c6.4 0 10 6.5 10 6.5a17 17 0 0 1-3.3 4M6.3 7.9A16.8 16.8 0 0 0 2 12s3.6 6.5 10 6.5c1.2 0 2.3-.2 3.3-.5" />
                      </svg>
                    )}
                  </button>
                </span>
                <span className="co-hint">Between 6 and 12 characters.</span>
              </div>

              {data.NumberOfCountries > 0 && (
                <div className="co-field co-field-wide">
                  <label className="co-label">
                    Countries you get access to
                  </label>
                  <MultiSelect
                    hasSelectAll={false}
                    options={appCountriesList}
                    value={selected}
                    onChange={setSelected}
                    labelledBy="Select"
                  />
                  <span className="co-hint">
                    {'This plan includes ' + data.NumberOfCountries +
                      (data.NumberOfCountries === 1 ? ' country' : ' countries') +
                      '. Selected ' + selected.length + ' of ' + data.NumberOfCountries + '.'}
                  </span>
                </div>
              )}
            </div>

            <div className="co-actions">
              {note && (
                <div className={note.tone === 'bad' ? 'co-note co-note-bad' : 'co-note co-note-good'} role="alert">
                  <b>{note.title}</b>
                  <span>{note.text}</span>
                </div>
              )}

              <button className="co-pay" type="submit" disabled={submitting || isLoading}>
                {submitting ? (
                  <>
                    <span className="co-spin" style={{ width: 18, height: 18, borderWidth: 2 }} aria-hidden="true"></span>
                    Taking you to Stripe…
                  </>
                ) : (
                  <>Pay {data.Price}€ with Stripe</>
                )}
              </button>

              <p className="co-terms">
                By completing this payment you agree to our Terms of Use.
                You will be redirected to Stripe; your card details are never handled by 123High.
              </p>
            </div>
          </form>
        </section>
      </div>
    </div>
  );
};

export default Checkout;
